// pages/chart/layouts/presets.ts — built-in dashboard layout presets (design §7)
//
// PRESET_LAYOUTS is the single source of truth for built-in layouts. Presets are
// NEVER persisted (dashboardStore partialize filters !isPreset) — they re-seed
// from this file on every load so preset code updates always ship (§9.2, M7).
//
// The '__ACTIVE__' sentinel is resolved at RENDER time (§5.5/§9.3) — never written
// back into config. Phase 1 registers 'price-chart' (+ 'ticker-stats'); every other
// referenced panel type falls back to the UNKNOWN_PANEL renderer so the grid still
// lays out cleanly until later phases register the real components.
//
// honors exactOptionalPropertyTypes: optional config fields are omitted, never
// assigned undefined. createdAt: 0 marks the immutable preset epoch.

import type { LayoutDefinition } from '../modular/dashboardTypes';

/** Sentinel resolved to the active symbol at render time (§5.5). */
const ACTIVE = '__ACTIVE__';

// --- simple (beginner / first visit): price-chart + trade-ticket, group sg-1 ---
const SIMPLE: LayoutDefinition = {
  id: 'simple',
  name: 'Simple',
  isPreset: true,
  createdAt: 0,
  grid: [
    { i: 'chart', x: 0, y: 0, w: 9, h: 20, minW: 4, minH: 8 },
    { i: 'ticket', x: 9, y: 0, w: 3, h: 20, minW: 3, minH: 8 },
  ],
  panelConfigs: {
    chart: {
      type: 'price-chart',
      title: 'Price',
      config: {
        symbol: ACTIVE,
        interval: 15,
        syncGroup: 'sg-1',
        syncRole: 'anchor',
        indicators: { emas: [9, 21], vwap: false },
      },
    },
    ticket: {
      type: 'trade-ticket',
      title: 'Trade',
      config: {
        symbol: ACTIVE,
        syncGroup: 'sg-1',
        syncRole: 'symbol-only',
        marketType: 'spot',
      },
    },
  },
};

// --- trading (DEFAULT, active spot trader): §7.2 worked JSON, group main ---
const TRADING: LayoutDefinition = {
  id: 'trading',
  name: 'Trading',
  isPreset: true,
  createdAt: 0,
  grid: [
    // Watchlist slim on the left (2), the Price chart dominates the centre (8), and the
    // book/trades/ticket/stats stack in a single 2-wide column on the right so the chart is
    // readable on a Retina display instead of being squeezed by the side panels.
    { i: 'wl', x: 0, y: 0, w: 2, h: 28, minW: 2, minH: 8 },
    { i: 'chart', x: 2, y: 0, w: 8, h: 16, minW: 4, minH: 8 },
    { i: 'rsi', x: 2, y: 16, w: 8, h: 4, minW: 4, minH: 3 },
    { i: 'macd', x: 2, y: 20, w: 8, h: 4, minW: 4, minH: 3 },
    { i: 'depth', x: 2, y: 24, w: 8, h: 8, minW: 3, minH: 6 },
    { i: 'book', x: 10, y: 0, w: 2, h: 9, minW: 2, minH: 6 },
    { i: 'tape', x: 10, y: 9, w: 2, h: 9, minW: 2, minH: 6 },
    { i: 'ticket', x: 10, y: 18, w: 2, h: 6, minW: 2, minH: 6 },
    { i: 'stats', x: 10, y: 24, w: 2, h: 4, minW: 2, minH: 3 },
  ],
  panelConfigs: {
    wl: {
      type: 'watchlist-panel',
      title: 'Watchlist',
      config: { syncGroup: 'main', syncRole: 'driver' },
    },
    chart: {
      type: 'price-chart',
      title: 'Price',
      config: {
        symbol: ACTIVE,
        interval: 15,
        syncGroup: 'main',
        indicators: { emas: [9, 21], vwap: false },
      },
    },
    rsi: {
      type: 'indicator-pane',
      title: 'RSI (14)',
      config: {
        symbol: ACTIVE,
        interval: 15,
        syncGroup: 'main',
        indicator: 'rsi',
        params: { period: 14 },
      },
    },
    macd: {
      type: 'indicator-pane',
      title: 'MACD',
      config: {
        symbol: ACTIVE,
        interval: 15,
        syncGroup: 'main',
        indicator: 'macd',
      },
    },
    depth: {
      type: 'depth-chart',
      title: 'Depth',
      config: { symbol: ACTIVE, syncGroup: 'main' },
    },
    book: {
      type: 'order-book',
      title: 'Order Book',
      config: { symbol: ACTIVE, syncGroup: 'main' },
    },
    tape: {
      type: 'market-trades',
      title: 'Trades',
      config: { symbol: ACTIVE, syncGroup: 'main' },
    },
    ticket: {
      type: 'trade-ticket',
      title: 'Trade',
      config: {
        symbol: ACTIVE,
        syncGroup: 'main',
        syncRole: 'symbol-only',
        marketType: 'spot',
      },
    },
    stats: {
      type: 'ticker-stats',
      title: 'Stats',
      config: { symbol: ACTIVE, syncGroup: 'main', variant: 'full' },
    },
  },
};

// --- multichart-2x2 (multi-timeframe trader): §7.3 worked JSON, group mc ---
const MULTICHART_2X2: LayoutDefinition = {
  id: 'multichart-2x2',
  name: 'Multi-Chart 2x2',
  isPreset: true,
  createdAt: 0,
  grid: [
    { i: 'mc-1m', x: 0, y: 0, w: 6, h: 8, minW: 3, minH: 4 },
    { i: 'mc-15m', x: 6, y: 0, w: 6, h: 8, minW: 3, minH: 4 },
    { i: 'mc-1h', x: 0, y: 8, w: 6, h: 8, minW: 3, minH: 4 },
    { i: 'mc-1d', x: 6, y: 8, w: 6, h: 8, minW: 3, minH: 4 },
  ],
  panelConfigs: {
    'mc-1m': {
      type: 'price-chart',
      title: '1m',
      config: { symbol: ACTIVE, interval: 1, syncGroup: 'mc', indicators: { vwap: false } },
    },
    'mc-15m': {
      type: 'price-chart',
      title: '15m',
      config: { symbol: ACTIVE, interval: 15, syncGroup: 'mc', indicators: { emas: [20, 50] } },
    },
    'mc-1h': {
      type: 'price-chart',
      title: '1h',
      config: {
        symbol: ACTIVE,
        interval: 60,
        syncGroup: 'mc',
        indicators: {},
        subPaneIndicator: 'rsi',
      },
    },
    'mc-1d': {
      type: 'price-chart',
      title: '1d',
      config: { symbol: ACTIVE, interval: 1440, syncGroup: 'mc', indicators: { emas: [50, 200] } },
    },
  },
  syncOverrides: { mc: { symbol: true, interval: false, crosshair: true, timeRange: true } },
};

// --- multichart-symbols (multi-asset monitor): BTC/ETH/SOL/XRP @15m, group mc-grid ---
// symbol NOT synced (4 distinct symbols → 4 refcounts), so these carry EXPLICIT
// raw Kraken codes, not the __ACTIVE__ sentinel (§7.1, §7.3 note).
const MULTICHART_SYMBOLS: LayoutDefinition = {
  id: 'multichart-symbols',
  name: 'Multi-Chart Symbols',
  isPreset: true,
  createdAt: 0,
  grid: [
    { i: 'mc-btc', x: 0, y: 0, w: 6, h: 8, minW: 3, minH: 4 },
    { i: 'mc-eth', x: 6, y: 0, w: 6, h: 8, minW: 3, minH: 4 },
    { i: 'mc-sol', x: 0, y: 8, w: 6, h: 8, minW: 3, minH: 4 },
    { i: 'mc-xrp', x: 6, y: 8, w: 6, h: 8, minW: 3, minH: 4 },
  ],
  panelConfigs: {
    'mc-btc': {
      type: 'price-chart',
      title: 'BTC/USD',
      config: { symbol: 'XXBTZUSD', interval: 15, syncGroup: 'mc-grid', indicators: {} },
    },
    'mc-eth': {
      type: 'price-chart',
      title: 'ETH/USD',
      config: { symbol: 'XETHZUSD', interval: 15, syncGroup: 'mc-grid', indicators: {} },
    },
    'mc-sol': {
      type: 'price-chart',
      title: 'SOL/USD',
      config: { symbol: 'SOLUSD', interval: 15, syncGroup: 'mc-grid', indicators: {} },
    },
    'mc-xrp': {
      type: 'price-chart',
      title: 'XRP/USD',
      config: { symbol: 'XXRPZUSD', interval: 15, syncGroup: 'mc-grid', indicators: {} },
    },
  },
  syncOverrides: { 'mc-grid': { symbol: false, interval: true, crosshair: true, timeRange: true } },
};

// --- terminal (pro market-monitoring desk): §7.1 catalog, group main ---
// Dense 12-col terminal: watchlist driver, two ticker-stats strips (active +
// BTC reference), a small price chart, and a depth + book + tape stack. All on
// group 'main' (symbol+crosshair+timeRange sync); the BTC reference stats panel
// carries an EXPLICIT code so it never tracks the active symbol (symbol-only
// role keeps it pinned).
const TERMINAL: LayoutDefinition = {
  id: 'terminal',
  name: 'Terminal',
  isPreset: true,
  createdAt: 0,
  grid: [
    { i: 'wl', x: 0, y: 0, w: 2, h: 22, minW: 2, minH: 8 },
    { i: 'stats-active', x: 2, y: 0, w: 6, h: 2, minW: 2, minH: 2 },
    { i: 'stats-btc', x: 8, y: 0, w: 4, h: 2, minW: 2, minH: 2 },
    { i: 'chart', x: 2, y: 2, w: 6, h: 12, minW: 4, minH: 6 },
    { i: 'depth', x: 2, y: 14, w: 6, h: 8, minW: 3, minH: 6 },
    { i: 'book', x: 8, y: 2, w: 2, h: 20, minW: 2, minH: 6 },
    { i: 'tape', x: 10, y: 2, w: 2, h: 20, minW: 2, minH: 4 },
  ],
  panelConfigs: {
    wl: {
      type: 'watchlist-panel',
      title: 'Watchlist',
      config: { syncGroup: 'main', syncRole: 'driver' },
    },
    'stats-active': {
      type: 'ticker-stats',
      title: 'Stats',
      config: { symbol: ACTIVE, syncGroup: 'main', variant: 'full' },
    },
    'stats-btc': {
      type: 'ticker-stats',
      title: 'BTC/USD',
      config: { symbol: 'XXBTZUSD', variant: 'compact' },
    },
    chart: {
      type: 'price-chart',
      title: 'Price',
      config: {
        symbol: ACTIVE,
        interval: 5,
        syncGroup: 'main',
        indicators: { emas: [9, 21], vwap: false },
      },
    },
    depth: {
      type: 'depth-chart',
      title: 'Depth',
      config: { symbol: ACTIVE, syncGroup: 'main' },
    },
    book: {
      type: 'order-book',
      title: 'Order Book',
      config: { symbol: ACTIVE, syncGroup: 'main' },
    },
    tape: {
      type: 'market-trades',
      title: 'Trades',
      config: { symbol: ACTIVE, syncGroup: 'main' },
    },
  },
};

// --- forecast-research (analyst desk): §7.1 catalog, group main ---
// Price chart + 7-horizon forecast table + active stats and two indicator
// sub-panes (RSI + MACD) — everything on group 'main' so the forecast/indicators
// follow the symbol + interval the chart drives.
const FORECAST_RESEARCH: LayoutDefinition = {
  id: 'forecast-research',
  name: 'Forecast Research',
  isPreset: true,
  createdAt: 0,
  grid: [
    { i: 'chart', x: 0, y: 0, w: 8, h: 12, minW: 4, minH: 6 },
    { i: 'rsi', x: 0, y: 12, w: 8, h: 4, minW: 4, minH: 3 },
    { i: 'macd', x: 0, y: 16, w: 8, h: 4, minW: 4, minH: 3 },
    { i: 'forecast', x: 8, y: 0, w: 4, h: 13, minW: 3, minH: 5 },
    { i: 'stats', x: 8, y: 13, w: 4, h: 7, minW: 2, minH: 2 },
  ],
  panelConfigs: {
    chart: {
      type: 'price-chart',
      title: 'Price',
      config: {
        symbol: ACTIVE,
        interval: 60,
        syncGroup: 'main',
        indicators: { emas: [9, 21, 50], vwap: false },
      },
    },
    rsi: {
      type: 'indicator-pane',
      title: 'RSI (14)',
      config: {
        symbol: ACTIVE,
        interval: 60,
        syncGroup: 'main',
        indicator: 'rsi',
        params: { period: 14 },
      },
    },
    macd: {
      type: 'indicator-pane',
      title: 'MACD',
      config: {
        symbol: ACTIVE,
        interval: 60,
        syncGroup: 'main',
        indicator: 'macd',
      },
    },
    forecast: {
      type: 'forecast-panel',
      title: 'Forecast',
      config: {
        symbol: ACTIVE,
        interval: 60,
        syncGroup: 'main',
        showDeterministic: true,
        showMl: true,
      },
    },
    stats: {
      type: 'ticker-stats',
      title: 'Stats',
      config: { symbol: ACTIVE, syncGroup: 'main', variant: 'full' },
    },
  },
};

// --- overview-mobile (phone single-column scan): §7.1 catalog, group main ---
// Single 12-wide column that stacks top→bottom in tap order: active stats →
// market heatmap → watchlist → price chart. Heatmap + watchlist drive the active
// symbol on group 'main'; tapping a tile/row swaps what stats + chart show.
const OVERVIEW_MOBILE: LayoutDefinition = {
  id: 'overview-mobile',
  name: 'Overview (Mobile)',
  isPreset: true,
  createdAt: 0,
  grid: [
    { i: 'stats', x: 0, y: 0, w: 12, h: 3, minW: 2, minH: 2 },
    { i: 'heatmap', x: 0, y: 3, w: 12, h: 8, minW: 3, minH: 2 },
    { i: 'wl', x: 0, y: 11, w: 12, h: 10, minW: 2, minH: 8 },
    { i: 'chart', x: 0, y: 21, w: 12, h: 10, minW: 4, minH: 6 },
  ],
  panelConfigs: {
    stats: {
      type: 'ticker-stats',
      title: 'Stats',
      config: { symbol: ACTIVE, syncGroup: 'main', variant: 'full' },
    },
    heatmap: {
      type: 'market-overview-heatmap',
      title: 'Market Overview',
      config: { syncGroup: 'main', filterQuote: 'USD', sortBy: 'change' },
    },
    wl: {
      type: 'watchlist-panel',
      title: 'Watchlist',
      config: { syncGroup: 'main', syncRole: 'driver' },
    },
    chart: {
      type: 'price-chart',
      title: 'Price',
      config: {
        symbol: ACTIVE,
        interval: 15,
        syncGroup: 'main',
        indicators: { emas: [9, 21] },
      },
    },
  },
};

/**
 * Built-in layouts keyed by id. 'trading' is the hardcoded default activeLayout
 * (§9.2.1) and the fallback target for deleteLayout/crash-safe merge — it MUST
 * always be present. Presets re-seed from here every load (never persisted).
 */
export const PRESET_LAYOUTS: Record<string, LayoutDefinition> = {
  simple: SIMPLE,
  trading: TRADING,
  'multichart-2x2': MULTICHART_2X2,
  'multichart-symbols': MULTICHART_SYMBOLS,
  terminal: TERMINAL,
  'forecast-research': FORECAST_RESEARCH,
  'overview-mobile': OVERVIEW_MOBILE,
};
